import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

import styled from "styled-components/macro";

import { faTimesCircle } from "@fortawesome/pro-light-svg-icons";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/pro-regular-svg-icons";
import { DateTime } from "luxon";

import { FnrPopoverWithScroll } from "UI/FnrPopover2";

import "./pickerStyles.css";

const years = () => {
  let year = DateTime.now().year;
  let count = 0;
  let years = [];

  while (count < 6) {
    years.push(year + count);
    count++;
  }

  return years;
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export const startDateText = (startDate) => {
  if (!startDate) return "now";

  let date = DateTime.fromISO(startDate).toLocaleString(DateTime.DATE_MED);
  let today = DateTime.now().toLocaleString(DateTime.DATE_MED);
  let tomorrow = DateTime.now()
    .plus({ days: 1 })
    .toLocaleString(DateTime.DATE_MED);

  if (date === today) return "now";
  if (date === tomorrow) return "tomorrow";

  return date;
};

export const endDateText = (endDate) => {
  if (!endDate) return "I cancel";

  let date = DateTime.fromISO(endDate);
  return `${date.toLocaleString(DateTime.DATE_MED)}`;
};

export default function RecurringDatePicker({
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  accentColor,
  startDateWord = "Starting",
  includeIcons = true,
}) {
  const handleStartDateSelect = (date) => {
    setStartDate(date);

    if (endDate && endDate < date) {
      /**
       * If they user sets the end date first,
       * then sets the start date past than the end date,
       * then set the end date to the start date
       * so that the end date cannot be before the start date
       */
      setEndDate(date);
    }
  };

  const handleEndDateSelect = (date) => {
    setEndDate(date);
  };

  return (
    <Wrapper data-cy="recurringDatePicker" className="recurring_date_pickers">
      {startDateWord}
      <Picker
        date={startDate}
        handleSelect={handleStartDateSelect}
        text={startDateText(startDate)}
        linkColor={accentColor}
        includeIcon={includeIcons}
        testTag="recurringStartDatePicker"
      />
      and going until
      <Picker
        date={endDate}
        handleSelect={handleEndDateSelect}
        text={endDateText(endDate)}
        canClear={true}
        linkColor={accentColor}
        minDate={startDate}
        includeIcon={includeIcons}
        testTag="recurringEndDatePicker"
      />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  display: inline;
  z-index: 250;
  line-height: 24px;

  > div {
    display: inline;
  }

  @media screen and (max-width: 480px) {
    margin-left: 0;
  }
`;

const Picker = ({
  date,
  handleSelect,
  text,
  canClear,
  linkColor,
  minDate,
  includeIcon,
  testTag = "datePickerBtn",
}) => {
  const btnRef = React.useRef();
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((open) => !open);

  const handleChange = (date) => {
    let dateString = DateTime.fromJSDate(date).toISODate();

    toggle();
    handleSelect(dateString);
  };

  const handleClear = () => {
    setOpen(false);
    handleSelect(null);
  };

  const now = new Date();
  const minimumDate = minDate ? DateTime.fromISO(minDate).toJSDate() : now;
  const fiveYearsFromNow = new Date();
  fiveYearsFromNow.setFullYear(now.getFullYear() + 5);

  return (
    <>
      <Text
        data-cy={testTag}
        ref={btnRef}
        onClick={toggle}
        color={linkColor}
        open={open}
      >
        {text}
        {includeIcon && <Icon className="fad fa-calendar-day" />}
      </Text>

      <FnrPopoverWithScroll
        buttonRef={btnRef}
        show={open}
        onShouldClose={() => setOpen(false)}
        color="white"
      >
        <OurDatePicker
          $selectedBackgroundColor={"red"}
          inline
          selected={date ? DateTime.fromISO(date).toJSDate() : date}
          onChange={handleChange}
          className="picker-position"
          calendarClassName="no-float"
          minDate={minimumDate || now}
          maxDate={fiveYearsFromNow}
          calendarContainer={canClear ? EndDateCalendar : StartDateCalendar}
          peekNextMonth
          // showMonthDropdown={true}
          // showYearDropdown={true}
          dropdownMode="select"
          renderCustomHeader={({
            date,
            changeYear,
            changeMonth,
            decreaseMonth,
            increaseMonth,
            prevMonthButtonDisabled,
            nextMonthButtonDisabled,
          }) => {
            return (
              <PickerHeader>
                {}
                <MonthNavButton
                  data-cy="recurringPrevMonthBtn"
                  onClick={decreaseMonth}
                  disabled={prevMonthButtonDisabled}
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </MonthNavButton>

                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "18px",
                  }}
                >
                  <PickerHeaderMonth>
                    {months[DateTime.fromJSDate(date).month - 1]}
                  </PickerHeaderMonth>

                  <YearSelect
                    value={DateTime.fromJSDate(date).year}
                    onChange={({ target: { value } }) => {
                      changeYear(value);
                    }}
                  >
                    {years().map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </YearSelect>
                </div>

                <MonthNavButton
                  data-cy="recurringNextMonthBtn"
                  onClick={increaseMonth}
                  disabled={nextMonthButtonDisabled}
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </MonthNavButton>
              </PickerHeader>
            );
          }}
        >
          {canClear && date && (
            <ClearWrapper>
              <ClearButton onClick={handleClear} color={"#216ba5"}>
                <FontAwesomeIcon
                  icon={faTimesCircle}
                  style={{ marginRight: "8px" }}
                />
                Clear selection
              </ClearButton>
            </ClearWrapper>
          )}
        </OurDatePicker>
      </FnrPopoverWithScroll>
    </>
  );
};

const styles = {
  background: "hsl(0 0% 90%)",
  textAlign: "center",
  fontWeight: 600,
  paddingTop: "5px",
  fontSize: "12px",
  borderTopLeftRadius: ".5rem",
  borderTopRightRadius: ".5rem",
  color: "hsl(0 0% 50%)",
};

const StartDateCalendar = ({ className, children }) => {
  return (
    <div>
      <div className={className}>
        <div style={styles}>Starting Date</div>
        <div style={{ position: "relative" }}>{children}</div>
      </div>
    </div>
  );
};
const EndDateCalendar = ({ className, children }) => {
  return (
    <div>
      <div className={className}>
        <div style={styles}>Ending Date</div>
        <div style={{ position: "relative" }}>{children}</div>
      </div>
    </div>
  );
};

const Text = styled.button`
  color: ${(props) => props.color};
  font-size: 16px;
  font-weight: 600;
  background: transparent;
  white-space: nowrap;
  border: 1px solid transparent;
  ${({ open, color }) =>
    open &&
    `border: 1px solid ${color};
    background: ${color};
    color: #FFF;
    `}
  border-radius: 8px;
  cursor: pointer;
  padding: 0 4px;
  margin: 0 2px;

  transition: all 0.6s;

  &:hover {
    transition: border 0.2s;
    border: 1px solid
      ${({ open, color }) => (open ? color : "rgba(190, 195, 199, 1)")};
    border-radius: 8px;
  }

  &:focus-within {
    transition: border 0.2s;
    border: 1px solid
      ${({ open, color }) => (open ? color : "rgba(190, 195, 199, 1)")};
    border-radius: 8px;
  }
`;

const Icon = styled.span`
  margin-left: 4px;
`;

const ClearWrapper = styled.div`
  display: grid;
  place-items: center;
  padding-bottom: 8px;
  padding-top: 6px;
  border-top: 1px solid #aeaeae;
`;

const ClearButton = styled.button`
  background: transparent;
  border: none;
  color: ${(props) => props.color || "#278dc1"};
  font-weight: 400;
  transition: all 0.2s ease-in-out;
  display: flex;
  justify-content: center;
  align-items: center;

  &:hover {
    cursor: pointer;
    transform: scale(1.1);
    color: ${(props) => props.color || "#1f7eaf"};
  }
`;

const MonthNavButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;

  &:disabled {
    opacity: 0;
    cursor: unset;
  }
`;

const PickerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 10px 10px 10px;
`;

const PickerHeaderMonth = styled.div`
  font-weight: 600;
`;

const YearSelect = styled.select`
  background: transparent;
  border: none;
  font-weight: 600;
  font-size: 18px;

  &:hover {
    cursor: pointer;
  }
`;

const OurDatePicker = styled(DatePicker)`
  .react-datepicker__day--selected {
    background-color: ${(props) => props.$selectedBackgroundColor || "inherit"};
  }
`;
